import { useFeedbackLoader } from "./Feedback.support";
import {
  ArticleRounded as CardsIcon,
  ViewListRounded as TableIcon,
  AutoAwesomeRounded as NewIcon,
  BoltRounded as EnhancementIcon,
  ConstructionRounded as BugIcon,
  QuestionMarkRounded as UnknownTypeIcon,
  SaveAltRounded as DownloadIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import * as dates from "common/dates";
import * as strings from "common/strings";
import Component from "components/Component";
import Button from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import InlineSearch from "components/common/InlineSearch";
import Markdown from "components/common/Markdown";
import { useState } from "react";
import * as submissionsService from "services/submissionsService";

const feedbackTypeConfig = {
  NEW_FEATURE_REQUEST: {
    label: "New Feature",
    icon: (
      <NewIcon
        className={"no-print"}
        sx={{
          padding: "3px",
          borderRadius: "50%",
          background: "mediumseagreen",
          fill: "white",
        }}
      />
    ),
  },
  ENHANCEMENT_REQUEST: {
    label: "Enhancement",
    icon: (
      <EnhancementIcon
        className={"no-print"}
        sx={{
          padding: "2px",
          borderRadius: "50%",
          background: "darkorange",
          fill: "white",
        }}
      />
    ),
  },
  BUG_REPORT: {
    label: "Bug",
    icon: (
      <BugIcon
        className={"no-print"}
        sx={{
          padding: "2px",
          borderRadius: "50%",
          background: "dodgerblue",
          fill: "white",
        }}
      />
    ),
  },
};

const FeedbackCard = ({ feedback }) => {
  const emailMeta = strings.extractNameFromEmail(feedback.userEmail);
  const color = strings.stringToColor(feedback.userEmail);
  const typeConfig = feedbackTypeConfig[feedback.type] ?? {
    label: "?",
    icon: <UnknownTypeIcon />,
  };

  return (
    <Card elevation={0}>
      <CardContent>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          marginBottom={2}
        >
          <Tooltip title={feedback.userEmail}>
            <Stack direction={"row"} alignItems={"center"} gap={1}>
              <Avatar
                sx={{
                  width: "2rem",
                  height: "2rem",
                  fontSize: "0.875rem",
                  bgcolor: color,
                }}
              >
                {emailMeta.initials}
              </Avatar>
              <Stack>
                <Typography variant={"subtitle2"} sx={{ color: color }}>
                  {emailMeta.name}
                </Typography>
                <Typography
                  variant={"subtitle2"}
                  sx={{ marginTop: "-3px !important", fontSize: "0.75rem" }}
                >
                  {dates.readableDate(feedback.timestamp, {
                    includeTime: true,
                  })}
                </Typography>
              </Stack>
            </Stack>
          </Tooltip>

          <Chip
            size={"small"}
            variant={"outlined"}
            label={typeConfig.label}
            icon={typeConfig.icon}
            sx={{ fontSize: "0.75rem" }}
          />
        </Stack>
        <Markdown value={feedback.message ?? "No description was provided"} />
      </CardContent>
    </Card>
  );
};

const CardsView = ({ data }) => {
  return data.map((x, index) => (
    <FeedbackCard key={x?.id ?? index} feedback={x} />
  ));
};

const FeedbackRow = ({ feedback }) => {
  const typeConfig = feedbackTypeConfig[feedback.type] ?? {
    label: "?",
    icon: <UnknownTypeIcon />,
  };

  return (
    <TableRow sx={{ verticalAlign: "top" }}>
      <TableCell>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          {typeConfig.icon}
          {typeConfig.label}
        </Stack>
      </TableCell>
      <TableCell>
        <Markdown value={feedback.message ?? "No description was provided"} />
      </TableCell>
      <TableCell>{feedback.userEmail}</TableCell>
      <TableCell>{dates.toISODateString(feedback.timestamp)}</TableCell>
    </TableRow>
  );
};

const TableView = ({ data, isLoading, timestamp }) => {
  const escapeCsvContent = (value) => {
    if (value == null) return "";
    const escaped = String(value).replace(/"/g, `""`);
    return `"${escaped}"`;
  };

  const formatDate = (
    dateString,
    { includeTime = false, delimiter = "-" } = {}
  ) => {
    if (String(dateString ?? "").trim() === "") return "";
    const date = new Date(dateString);
    const pad = (n) => String(n).padStart(2, 0);
    let formatted = `${date.getFullYear()}${delimiter}${pad(
      date.getMonth() + 1
    )}${delimiter}${pad(date.getDate())}`;
    if (includeTime)
      formatted += ` ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
        date.getSeconds()
      )}`;
    return formatted;
  };

  const toCsvString = () => {
    const header = ["Type", "Description", "Reported By", "Date"];
    const rows = [
      header.join(","),
      ...data.map((x) =>
        [
          (feedbackTypeConfig[x.type] ?? { label: x.type }).label,
          escapeCsvContent(x.message),
          x.userEmail,
          formatDate(x.timestamp, { includeTime: true }),
        ].join(",")
      ),
    ];
    return rows.join("\n");
  };

  const handleDownload = () => {
    const blob = new Blob([toCsvString()], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Feedback_${formatDate(timestamp, { delimiter: "_" })}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Component
      title={"Feedback"}
      subtitle={
        timestamp
          ? `As of ${dates.asOfDate(timestamp, { includeDayOfWeek: true })}`
          : undefined
      }
      options={
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          isLoading={isLoading}
        >
          {"Download CSV"}
        </Button>
      }
    >
      <TableContainer>
        <TableHead>
          <TableRow>
            <TableCell>{"Type"}</TableCell>
            <TableCell>{"Description"}</TableCell>
            <TableCell>{"Reported By"}</TableCell>
            <TableCell>{"Date"}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((x, index) => (
            <FeedbackRow key={x?.id ?? index} feedback={x} />
          ))}
        </TableBody>
      </TableContainer>
    </Component>
  );
};

const Loader = () => (
  <Stack alignItems={"center"} margin={2}>
    <DefaultLoader
      type={"TailSpin"}
      size={48}
      style={{ marginTop: "0.5rem" }}
    />
    <Typography className={"pulse"} color={"secondary"} mt={1}>
      {"Loading feedback data"}
    </Typography>
  </Stack>
);

const BlankSlate = ({ message }) => {
  return (
    <Paper elevation={0}>
      <Stack
        alignItems={"center"}
        justifyContent={"center"}
        sx={{ padding: "4rem" }}
      >
        <Typography color={"grey !important"}>{message}</Typography>
      </Stack>
    </Paper>
  );
};

const Feedback = () => {
  const [cardMode, setCardMode] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [refreshTimestamp] = useState(new Date().toISOString());

  const [listFeedback] = submissionsService.useLazyListFeedbackQuery();

  const { data: allData, isLoading } = useFeedbackLoader(listFeedback);

  const filteredData =
    searchText === ""
      ? allData
      : allData.filter((x) =>
          `${x.userEmail}|${feedbackTypeConfig[x.type]?.label ?? ""}|${
            x.message
          }`
            .toLowerCase()
            .includes(searchText.toLowerCase())
        );

  const showNoMatches = searchText !== "" && filteredData.length === 0;
  const showNoData = !isLoading && searchText === "" && allData.length === 0;

  const containerSx = cardMode
    ? {
        minWidth: "min(38rem, 100%)",
        maxWidth: "38rem",
        margin: "0.75rem auto",
      }
    : { width: "100%", margin: "0.75rem" };

  return (
    <Stack gap={1} sx={containerSx}>
      <Paper
        elevation={0}
        sx={{ padding: "0.5rem", borderRadius: "0.5rem" }}
        className={"no-print"}
      >
        <Stack direction={"row"} gap={1}>
          <InlineSearch
            placeholder={"Search by description, person, or type"}
            onChange={(searchText) => setSearchText(searchText)}
            fullWidth
          />
          <ToggleButtonGroup
            size={"small"}
            value={cardMode ? "cards" : "table"}
            color={"primary"}
          >
            <ToggleButton
              value={"cards"}
              onClick={(_) => setCardMode(true)}
              sx={{ padding: "4px 6px" }}
            >
              <Tooltip title={"Cards View"}>
                <CardsIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value={"table"}
              onClick={(_) => setCardMode(false)}
              sx={{ padding: "4px 6px" }}
            >
              <Tooltip title={"Table View"}>
                <TableIcon />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>
      <Stack gap={1}>
        {isLoading && allData.length === 0 ? (
          <Loader />
        ) : cardMode ? (
          <CardsView data={filteredData} />
        ) : (
          !showNoMatches &&
          !showNoData && (
            <TableView
              data={filteredData}
              isLoading={isLoading}
              timestamp={refreshTimestamp}
            />
          )
        )}
        {showNoMatches && (
          <BlankSlate message={`No matches found for '${searchText}'`} />
        )}
        {showNoData && (
          <BlankSlate message={`Nobody's submitted any feedback yet`} />
        )}
        {/* Show loading indicator while fetching additional pages */}
        {isLoading && allData.length > 0 && (
          <Stack alignItems={"center"} margin={2}>
            <DefaultLoader
              type={"TailSpin"}
              size={32}
              style={{ marginTop: "0.5rem" }}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default Feedback;
